import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe } from '../core/translate.pipe';
import { DsButtonDirective, DsCardDirective, DsCheckboxDirective, DsInputDirective } from '../ui';
import { apiUrl } from '../core/api-client';
import { LocalizationService } from '../core/localization.service';
import {
  PlatformApi,
  type ConsumerOrder,
  type ConsumerProduct,
  type ConsumerProgress,
  type ConsumerReportSummary,
  type CurrentUser,
  type PublishedAssessment,
  type PrivacyRequestRecord,
} from '../core/platform-api';

@Component({
  selector: 'app-consumer-page',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    DsButtonDirective,
    DsCardDirective,
    DsCheckboxDirective,
    DsInputDirective,
  ],
  templateUrl: './consumer-page.html',
  styleUrl: './consumer-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConsumerPage {
  private readonly api = inject(PlatformApi);
  private readonly i18n = inject(LocalizationService);
  protected readonly user = signal<CurrentUser | null>(null);
  protected readonly assessments = signal<PublishedAssessment[]>([]);
  protected readonly products = signal<ConsumerProduct[]>([]);
  protected readonly progress = signal<ConsumerProgress | null>(null);
  protected readonly order = signal<ConsumerOrder | null>(null);
  protected readonly reports = signal<ConsumerReportSummary[]>([]);
  protected readonly privacyRequests = signal<PrivacyRequestRecord[]>([]);
  protected readonly busy = signal(false);
  protected readonly error = signal<string | null>(null);
  protected readonly email = new FormControl('demo.consumer@example.test', {
    nonNullable: true,
    validators: [Validators.required, Validators.email],
  });

  protected readonly purchaseConsent = new FormControl(false, {
    nonNullable: true,
    validators: [Validators.requiredTrue],
  });
  protected readonly termsConsent = new FormControl(false, {
    nonNullable: true,
    validators: [Validators.requiredTrue],
  });

  protected async login(): Promise<void> {
    if (this.email.invalid) return;
    await this.run(async () => {
      this.user.set(await this.api.developmentLogin(this.email.value));
      const [assessments, products, reports, privacyRequests] = await Promise.all([
        this.api.listPublishedAssessments(),
        this.api.listConsumerProducts(),
        this.api.listConsumerReports(),
        this.api.listPrivacyRequests(),
      ]);
      this.assessments.set(assessments);
      this.products.set(products);
      this.reports.set(reports);
      this.privacyRequests.set(privacyRequests);
    });
  }

  protected async start(assessmentId: string): Promise<void> {
    await this.run(async () =>
      this.progress.set(await this.api.startConsumerAssessment(assessmentId)),
    );
  }

  protected async answer(code: string): Promise<void> {
    const progress = this.progress();
    const item = progress?.currentItem;
    if (!progress || !item) return;
    await this.run(async () =>
      this.progress.set(
        await this.api.submitConsumerAnswer(progress.assessmentId, item.itemId, code),
      ),
    );
  }

  protected async buy(productId: string): Promise<void> {
    if (this.purchaseConsent.invalid || this.termsConsent.invalid) {
      this.purchaseConsent.markAsTouched();
      this.termsConsent.markAsTouched();
      return;
    }
    const reportId = this.progress()?.preview?.reportId;
    if (!reportId) return;
    await this.run(async () =>
      this.order.set(await this.api.createConsumerOrder(reportId, productId)),
    );
  }

  protected async completeDevelopmentPayment(): Promise<void> {
    const order = this.order();
    if (!order) return;
    await this.run(async () => {
      await this.api.completeDevelopmentOrder(order.id);
      this.reports.set(await this.api.listConsumerReports());
      this.order.set({ ...order, status: 'Paid' });
    });
  }

  protected reportUrl(reportId: string, format: 'html' | 'pdf'): string {
    return apiUrl(`/api/v1/consumer/reports/${reportId}/${format}`);
  }

  protected async requestPrivacy(type: 'Export' | 'Deletion' | 'Dispute'): Promise<void> {
    await this.run(async () => {
      await this.api.submitPrivacyRequest(
        type,
        type === 'Dispute' ? this.i18n.t('consumer.disputeReason') : undefined,
      );
      this.privacyRequests.set(await this.api.listPrivacyRequests());
    });
  }

  private async run(action: () => Promise<void>): Promise<void> {
    this.busy.set(true);
    this.error.set(null);
    try {
      await action();
    } catch {
      this.error.set(this.i18n.t('consumer.error'));
    } finally {
      this.busy.set(false);
    }
  }
}
